import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';

const reputationData = [
  { value: '500万+', label: '累计服务用户' },
  { value: '99.7%', label: '用户满意度' },
  { value: '28分钟', label: '平均送达时间' },
  { value: '12项', label: '服务品类' },
];

const faqData = [
  {
    q: '凌晨不打烊是24小时服务吗？',
    a: '是的，凌晨不打烊全年365天、每天24小时不间断提供服务。无论是深夜凌晨的配送需求，还是半夜的在线咨询，我们都有专人随时响应。我们的客服团队、配送团队和专业技术团队实行轮班制，确保任何时候都有人在线守候。',
  },
  {
    q: '凌晨配送服务覆盖哪些区域？',
    a: '目前凌晨不打烊的配送服务已覆盖全国主要一二线城市的核心城区，包括北京、上海、广州、深圳、杭州、成都、武汉等30+城市。我们正在不断扩展服务范围，您可以在下单时查看所在区域是否在覆盖范围内。',
  },
  {
    q: '如何保障深夜服务的安全和质量？',
    a: '安全是我们的首要承诺。所有服务人员均经过严格的背景审查、身份核验和专业培训后持证上岗。平台提供全程服务追踪、一键紧急联系、服务保险等多重安全保障。每次服务完成后，用户可进行评价，评价直接影响服务人员的接单权限。',
  },
  {
    q: '凌晨下单后多久能响应？',
    a: '咨询类服务通常在3分钟内响应；配送类服务根据距离和品类不同，最快20分钟、最晚60分钟内送达；维修类服务市区范围内30分钟内上门。所有时效承诺均有超时赔付保障。',
  },
  {
    q: '凌晨服务的价格会比白天贵吗？',
    a: '凌晨时段的部分服务会有小幅的夜间服务附加费（通常为10%-20%），这是因为我们需要为夜间工作的服务人员提供相应的补贴。但平台严格控制溢价幅度，确保价格透明合理。所有费用在下单前都会明确展示，不会有隐藏消费。',
  },
];

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) return;

    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9rgvvsnlsngrm4lmc10', {
        method: 'POST',
        body: formData,
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore parse error */ }
      if (res.ok && parsed?.code === 'OK') {
        alert('感谢您的咨询！我们会尽快与您联系。');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        alert(msg);
      }
    } catch {
      alert('网络异常，请稍后重试');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于凌晨不打烊',
    description: '凌晨不打烊是专业的24小时全天候服务平台，提供深夜下单、凌晨咨询、随时响应等不间断服务。了解我们的品牌故事、口碑声誉和联系方式。',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Brand Intro */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6 text-center">
              关于凌晨不打烊
            </h1>
            <div className="bg-background-50 rounded-xl border border-background-200/60 p-6 md:p-8 space-y-4">
              <p className="text-foreground-600 leading-relaxed">
                <strong className="text-foreground-900">凌晨不打烊</strong>成立于2023年，是中国领先的24小时全天候服务平台。我们的使命很简单：让每一个在凌晨需要帮助的人，都能获得及时、专业、温暖的响应。
              </p>
              <p className="text-foreground-600 leading-relaxed">
                创始团队来自互联网、物流、医疗和心理咨询等多个行业，因为共同的信念走到一起——深夜的无助不应该被忽视，凌晨的需求值得被认真对待。从最初只有5个人的小团队，到如今覆盖30+城市、拥有超过5000名专业服务人员的平台，凌晨不打烊用三年时间证明：深夜的温度可以被传递。
              </p>
              <p className="text-foreground-600 leading-relaxed">
                我们不仅是一个服务平台，更是深夜城市中的一盏灯。无论是凌晨配送、深夜咨询、紧急维修还是健康守护，凌晨不打烊始终坚守一个承诺：<strong className="text-primary-600">无论几点，都有人在。</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section id="reputation" className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 text-center">
              口碑声誉
            </h2>
            <p className="text-foreground-500 text-sm text-center mb-10">
              用数据说话，用口碑证明
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {reputationData.map((item, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-6 border border-background-200/60 text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-2">{item.value}</div>
                  <div className="text-sm text-foreground-500">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-foreground-500 text-sm text-center mb-8">
              有任何问题或建议？欢迎随时联系我们，凌晨也有人回复
            </p>

            <div className="bg-background-50 rounded-xl border border-background-200/60 p-6 md:p-8">
              <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">手机号</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的手机号（选填）"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">咨询主题</label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors cursor-pointer"
                  >
                    <option value="">请选择咨询主题</option>
                    <option value="service">服务咨询</option>
                    <option value="cooperation">商务合作</option>
                    <option value="complaint">投诉建议</option>
                    <option value="other">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请描述您的需求或问题（限500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">限500字以内</p>
                </div>

                <div className="honeypot-wrapper">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-white rounded-lg text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  提交咨询
                </button>
              </form>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center p-5 bg-background-50 rounded-xl border border-background-200/60">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                  <i className="ri-phone-line"></i>
                </div>
                <p className="text-sm font-medium text-foreground-900 mb-1">服务热线</p>
                <p className="text-sm text-foreground-500">400-888-0000</p>
              </div>
              <div className="text-center p-5 bg-background-50 rounded-xl border border-background-200/60">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                  <i className="ri-mail-line"></i>
                </div>
                <p className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</p>
                <p className="text-sm text-foreground-500">hello@lingchenbudaye.com</p>
              </div>
              <div className="text-center p-5 bg-background-50 rounded-xl border border-background-200/60">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                  <i className="ri-map-pin-line"></i>
                </div>
                <p className="text-sm font-medium text-foreground-900 mb-1">公司地址</p>
                <p className="text-sm text-foreground-500">北京市朝阳区建国路88号</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 text-center">
              常见问题
            </h2>
            <p className="text-foreground-500 text-sm text-center mb-10">
              关于凌晨不打烊，你可能想知道的一切
            </p>

            <div className="space-y-3">
              {faqData.map((item, i) => (
                <div key={i} className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">{item.q}</span>
                    <i
                      className={`ri-arrow-down-s-line text-foreground-400 transition-transform flex-shrink-0 ${
                        openFaq === i ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}